"use client";

/**
 * The connect panel (composer panels, spec 2026-09-19 §3.4): the card the
 * composer draws when an app needs the person's yes. It is the PAUSE card, not
 * a cousin of it (the founder, 2026-09-22: "same look, same icons with a
 * connect button" — a link in a sheet "is not what users want"), so the face
 * and the state machine are both AuthCard's: ConnectFace for the chrome,
 * useAuthConnectState for idle → connecting → connected and the walk back to
 * idle on an OAuth the person abandoned.
 *
 * What is this card's own: the url is the server's, minted at the connect (the
 * story's card reads it off a tool result), and a connected card folds itself
 * away instead of sitting on the screen as a done tick.
 */
import { useEffect, useEffectEvent, useMemo } from "react";
import type { ComposerPanel } from "@bash/shared";
import { ConnectFace } from "@/components/blocks/tools/AuthCard";
import { isHttpsUrl } from "@/components/composer/routerActions";
import { isDockWindow } from "@/lib/dock-window";
import { openExternalViaShell } from "@/lib/desktop-shell";
import { useAuthConnectState } from "@/hooks/useAuthConnectState";
import { hintedAppLabel } from "@/lib/account-display";
import {
  getIntegrationColor,
  resolveIntegrationDisplayName,
} from "@/lib/composio-tools";
import { makeNewAccountProbe } from "@/lib/connections-probe";

type Connect = Extract<ComposerPanel, { kind: "connect" }>;

/** The connected beat before the card folds away, the length of the inline
 *  card's own saved-to-Nami confirm: long enough to be read, short enough that
 *  a finished card is not still sitting there when they ask again. */
export const CONNECTED_LINGER_MS = 2500;

export function ConnectPanel({
  panel,
  onClose,
}: {
  panel: Connect;
  onClose: () => void;
}) {
  // The brand: our catalog first; the server's name only for a toolkit it does not know.
  const displayName = resolveIntegrationDisplayName(
    panel.toolkit,
    panel.displayName
  );
  const brandColor = getIntegrationColor(panel.toolkit);
  const addMode = panel.mode === "add";
  // Add-mode's "connected" must mean a NEW account appeared: the default probe
  // reads the ACTIVE sibling and would say Connected on mount. The probe holds
  // a baseline, so its identity is load-bearing both ways (AuthCard's note):
  // stable across renders, re-minted on a toolkit swap.
  // eslint-disable-next-line react-hooks/exhaustive-deps -- `toolkit` is a re-mint KEY for a stateful factory, not a captured value
  const newAccountProbe = useMemo(() => makeNewAccountProbe(), [panel.toolkit]);
  const { status, markClicked, resetToIdle } = useAuthConnectState(
    panel.toolkit,
    addMode ? newAccountProbe : undefined
  );

  // The deck rebuilds `onClose` on every render, so the fold reads the current
  // one through an Effect Event instead of restarting its beat each time.
  const fold = useEffectEvent(() => onClose());
  useEffect(() => {
    if (status !== "connected") return;
    const t = setTimeout(() => fold(), CONNECTED_LINGER_MS);
    return () => clearTimeout(t);
  }, [status]);

  const statusText =
    status === "connected"
      ? `${displayName} connected`
      : status === "connecting"
        ? `Connecting ${displayName}…`
        : addMode
          ? `Connect another ${displayName} account to continue`
          : `Connect ${hintedAppLabel(panel.accountHint, displayName)} to continue`;

  // The belt's own gate, applied where the card is drawn: a url the belt would
  // refuse is no card at all, never a Connect button with nothing behind it.
  if (!isHttpsUrl(panel.authUrl)) return null;

  return (
    <ConnectFace
      toolkit={panel.toolkit}
      brandColor={brandColor}
      status={status}
      statusText={statusText}
      onConnect={() => {
        markClicked();
        // The shell's Dock window has no external-open interceptor (AppShell
        // installs it; /dock mounts none), so window.open there would open an
        // invisible window: the shell's opener takes it to the system browser.
        if (isDockWindow() && openExternalViaShell(panel.authUrl)) return;
        // Popup blocker / no-op return: the OAuth window never opened, so the
        // card must not sit on a spinner forever.
        if (!window.open(panel.authUrl, "_blank")) resetToIdle();
      }}
      onClose={onClose}
      flush
      testId="connect-panel"
    />
  );
}
